import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { insertStudentProfile } from './create-student.js';
import { insertTeacherProfile } from './create-teacher.js';
import {
  canSendCode,
  discardPendingRegistration,
  isPendingRegistration,
  sendEmailVerification,
  sendExistingAccountNoticeIfDue,
  startPendingRegistration,
} from './email-verification.js';

/** The account createNewUser would refuse this address for: same email or username, case-insensitive. */
async function findAccountByEmail(email: string) {
  const selector = { $regex: `^${email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' };
  const users = await getRawCollection(AdornisUser._collectionName);
  return users.findOne(
    { $or: [{ email: selector }, { username: selector }] },
    { projection: { email: 1, signUpNoticeSentAt: 1, emailVerifiedAt: 1, emailVerification: 1 } },
  );
}

/**
 * The only way to create a password account. Replaces the client-side `registerUser` + create profile pair,
 * which logged the new account in before its address was confirmed. The account starts as a pending registration,
 * gets its profile and the code mail, and no session is opened: the user signs in by entering the code in this tab.
 */
export const signUpMutation = registerMutation({
  operationName: 'signUpMutation',
  type: () => Boolean,
  params: [
    { name: 'role', type: () => String },
    { name: 'firstName', type: () => String },
    { name: 'lastName', type: () => String },
    { name: 'school', type: () => String },
    { name: 'subjects', type: () => [String] },
    { name: 'email', type: () => String },
    { name: 'password', type: () => String },
    { name: 'language', type: () => String },
  ],
  resolve:
    (
      role: string,
      firstName: string,
      lastName: string,
      school: string,
      subjects: string[] | string,
      email: string,
      password: string,
      language = 'de',
    ) =>
    async () => {
      const normalizedEmail = email.trim();
      // Same checks the library's registerUser resolver runs before createNewUser.
      AdornisUser.passwordValidator({ value: password, key: 'password', target: null } as any);

      // An address that already has an account gets the same answer as a new one, so the form cannot be used to probe
      // who uses Gradias. Only the mailbox learns what happened, through a notice; the entered data is dropped.
      const existing = await findAccountByEmail(normalizedEmail);
      if (existing && !isPendingRegistration(existing)) {
        await sendExistingAccountNoticeIfDue(existing);
        return true;
      }
      // A pending registration belongs to nobody yet (email-verification.ts), so this one replaces it: the old profile
      // and password go unseen, and the new registration needs its own password plus the code from the mailbox. Only
      // the mailbox owner signing up gets both. Once all its codes are used, it stays until it expires.
      if (existing) {
        if (!canSendCode(existing)) return true;
        await discardPendingRegistration(existing._id);
      }

      const user = new AdornisUser({ username: normalizedEmail, email: normalizedEmail } as any);
      const userID = String(await createNewUser(user, password, false));
      await startPendingRegistration(userID, existing?.emailVerification);

      const profile = { userID, firstName, lastName, school, subjects, email: normalizedEmail, language };
      if (role === 'student') {
        await insertStudentProfile(profile);
      } else {
        await insertTeacherProfile(profile);
      }

      try {
        await sendEmailVerification({ _id: userID, email: normalizedEmail, firstName });
      } catch (err) {
        // The account exists either way; the code screen offers to send a new code.
        logger.error(
          { err, userID, context: 'EMAIL_VERIFICATION' },
          'Bestätigungs-Mail nach Registrierung fehlgeschlagen',
        );
      }
      return true;
    },
});
